import { generateClient } from 'aws-amplify/api';
import { I18n } from 'aws-amplify/utils';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import type { Reservation } from '@/features/driver/types/driveDetail';
import type { VehicleOperationScheduleType } from '@/features/vehicleOperationSchedule/types/vehicleOperationSchedule';
import * as subscriptions from '@/graphql/subscriptions';
import { apiGet } from '@/modules/hooks/use-query';
import { ServiceType } from '@/types/serviceType';

type UseRouteChangeSubscriptionParams = {
  vehicleId: string | null;
  vehicleOperationScheduleId: string | null;
  serviceType: ServiceType | undefined;
  reservationList: Reservation[];
  realTimeDetectionFlag: number;
  storybookMode: boolean;
  /** Storybookモック専用 */
  routeChangeModalShowFromMock: boolean;
  newReservationListFromMock: Reservation[];
  onError: (message: string) => void;
};

type UseRouteChangeSubscriptionReturn = {
  routeChangeModalShow: boolean;
  setRouteChangeModalShow: React.Dispatch<React.SetStateAction<boolean>>;
  newReservationList: Reservation[];
};

export const useRouteChangeSubscription = ({
  vehicleId,
  vehicleOperationScheduleId,
  serviceType,
  reservationList,
  realTimeDetectionFlag,
  storybookMode,
  routeChangeModalShowFromMock,
  newReservationListFromMock,
  onError,
}: UseRouteChangeSubscriptionParams): UseRouteChangeSubscriptionReturn => {
  const [routeChangeModalShow, setRouteChangeModalShow] = useState<boolean>(false);
  const [newReservationList, setNewReservationList] = useState<Reservation[]>([]);

  // Storybookモックからの初期化
  useEffect(() => {
    if (!storybookMode) return;
    setRouteChangeModalShow(routeChangeModalShowFromMock);
    setNewReservationList(newReservationListFromMock);
  }, [storybookMode, routeChangeModalShowFromMock, newReservationListFromMock]);

  const getNewReservationList = useCallback(
    async (id: string) => {
      try {
        const res = await apiGet(ApiNamePaths.drivesGetApi.apiName, `${ApiNamePaths.drivesGetApi.apiPath}/${id}`);
        const data = res as VehicleOperationScheduleType;
        if (data) {
          const newReservations = data.reservationList as unknown as Reservation[];
          const missingReservations = newReservations.filter(
            (newReservation) =>
              !reservationList.some(
                (existingReservation) => existingReservation.reservationId === newReservation.reservationId,
              ),
          );

          if (missingReservations.length > 0) {
            setRouteChangeModalShow(true);
            setNewReservationList(missingReservations);
          } else {
            setRouteChangeModalShow(true);
          }
          sessionStorage.setItem(StorageKeys.VEHICLE_OPERATION_SCHEDULE_ID, String(data.vehicleOperationScheduleId));
        }
      } catch (error) {
        const err = error as { message: string };
        onError(I18n.get(err.message) as string);
      }
    },
    [reservationList, onError],
  );

  const startSubscription = useCallback(() => {
    const currentDate = dayjs();
    const currentDateFormat = currentDate.format('YYYY-MM-DD');
    const client = generateClient();
    const variables = {
      filter: {
        vehicleId: { eq: vehicleId || '' },
        operationDate: { eq: currentDateFormat },
      },
    };

    const sub = client
      .graphql({ query: subscriptions.onUpdateVehicleOperationScheduleUpdateManagement, variables })
      .subscribe({
        next: ({ data }) => {
          if (
            data?.onUpdateVehicleOperationScheduleUpdateManagement?.firstVehicleOperationScheduleId &&
            String(data.onUpdateVehicleOperationScheduleUpdateManagement.firstVehicleOperationScheduleId) !==
              String(vehicleOperationScheduleId)
          ) {
            void getNewReservationList(
              String(data.onUpdateVehicleOperationScheduleUpdateManagement.firstVehicleOperationScheduleId),
            );
          }
        },
        error: () => {
          // 何もしない
        },
      });

    return () => {
      sub.unsubscribe();
    };
  }, [getNewReservationList, vehicleId, vehicleOperationScheduleId]);

  // リアルタイムオンデマンド時にサブスクリプションを開始
  useEffect(() => {
    if (storybookMode) return;
    if (
      serviceType !== ServiceType.RealtimeOnDemand ||
      !(realTimeDetectionFlag === 2 || (realTimeDetectionFlag === 1 && reservationList.length > 0))
    ) {
      return;
    }

    const cleanup = startSubscription();

    return cleanup;
  }, [startSubscription, serviceType, reservationList, realTimeDetectionFlag, storybookMode]);

  return { routeChangeModalShow, setRouteChangeModalShow, newReservationList };
};
